import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Save, User, Camera, Upload, X, Star, Info, Video, Loader2 } from 'lucide-react';
import { personService, locationService, cameraService } from '../../services';
import type { PersonType, AccessLevel, Location, CreatePersonData, FaceImage, Camera as CameraType } from '../../types';
import EZOPENPlayer from '../../components/ui/EZOPENPlayer';
import VideoPlayer from '../../components/ui/VideoPlayer';

interface PersonFormData {
  person_type: PersonType;
  first_name: string;
  last_name: string;
  employee_id: string;
  external_company: string;
  phone: string;
  email: string;
  access_level: AccessLevel;
  valid_from: string;
  valid_until: string;
  notes: string;
  location_ids: number[];
  is_active: boolean;
}

const initialFormData: PersonFormData = {
  person_type: 'EMPLOYEE',
  first_name: '',
  last_name: '',
  employee_id: '',
  external_company: '',
  phone: '',
  email: '',
  access_level: 'FULL',
  valid_from: '',
  valid_until: '',
  notes: '',
  location_ids: [],
  is_active: true,
};

const CreateEditPersonPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const mode = id ? 'edit' : 'create';
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<PersonFormData>(initialFormData);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(mode === 'edit');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Photo state (edit mode only)
  const [faceImages, setFaceImages] = useState<FaceImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadAsPrimary, setUploadAsPrimary] = useState(false);
  
  // Camera capture state
  const [cameras, setCameras] = useState<CameraType[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [cameraData, setCameraData] = useState<CameraType | null>(null);
  const [cameraStream, setCameraStream] = useState<any>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [selectedLocationForCamera, setSelectedLocationForCamera] = useState<string>('');

  // Fetch locations for selection
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await locationService.getLocations();
        setLocations(response.data || []);
      } catch (err) {
        console.error('Failed to fetch locations:', err);
      }
    };
    fetchLocations();
  }, []);

  // Fetch cameras when location selected for capture
  useEffect(() => {
    if (selectedLocationForCamera) {
      const fetchCameras = async () => {
        try {
          const response = await cameraService.getCamerasByLocation(parseInt(selectedLocationForCamera));
          // Filter to face recognition cameras
          const filtered = (response.data || []).filter(
            camera => camera.camera_purpose === 'face_recognition' || camera.camera_purpose === 'id_capture'
          );
          setCameras(filtered);
        } catch (err) {
          console.error('Failed to fetch cameras:', err);
          setCameras([]);
        }
      };
      fetchCameras();
    } else {
      setCameras([]);
      setSelectedCamera('');
      setCameraData(null);
      setCameraStream(null);
    }
  }, [selectedLocationForCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (capturedImage) {
        URL.revokeObjectURL(capturedImage);
      }
    };
  }, [capturedImage]);

  // Fetch camera stream when camera selected
  const fetchCameraStream = async (cameraId: number) => {
    try {
      // Find the camera data
      const camera = cameras.find(c => c.camera_id === cameraId);
      if (!camera) return;
      
      setCameraData(camera);
      
      // If it's a cloud camera, skip backend API - EZOPENPlayer handles it directly
      if (camera.cloud_provider) {
        console.log('Cloud camera detected - skipping backend API, EZOPENPlayer will handle');
        setCameraStream(null);
        return;
      }
      
      // For NVR cameras, get stream from backend
      const streamResponse = await cameraService.getCameraStream({
        camera_id: cameraId,
        stream_type: 'webrtc',
        stream_quality: 'main',
      });
      
      setCameraStream(streamResponse);
    } catch (err) {
      console.error('Failed to fetch camera stream:', err);
      setCameraStream(null);
    }
  };

  // Capture photo from camera using Canvas API
  const captureFromCamera = async () => {
    if (!selectedCamera || !cameraData) return;
    
    try {
      setIsCapturing(true);
      
      let blob: Blob;
      
      // For cloud cameras, capture from video element using Canvas API
      if (cameraData.cloud_provider) {
        console.log('📹 Capturing from cloud camera live feed...');
        
        // Get video element from EZOPEN player using DOM selector
        const playerContainer = document.querySelector('#face-camera-player');
        const videoElement = playerContainer?.querySelector('video') as HTMLVideoElement;
        
        console.log('Player container found:', !!playerContainer);
        console.log('Video element found:', !!videoElement);
        console.log('Video ready state:', videoElement?.readyState);
        
        if (!videoElement) {
          throw new Error('Video player not found. Please wait for the camera feed to load.');
        }
        
        // Wait if video is not ready
        if (videoElement.readyState < 2) {
          console.log('⏳ Video not ready yet, waiting 1 second...');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        if (videoElement.readyState >= 2) {
          // Capture from video element using canvas
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth || 640;
          canvas.height = videoElement.videoHeight || 480;
          const ctx = canvas.getContext('2d');
          
          console.log(`Canvas size: ${canvas.width}x${canvas.height}`);
          
          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }
          
          // Draw current video frame to canvas
          ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          
          // Convert canvas to blob
          blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((b) => {
              if (b) resolve(b);
              else reject(new Error('Failed to create blob from canvas'));
            }, 'image/jpeg', 0.95);
          });
          
          console.log('✅ Captured screenshot from cloud camera live feed');
        } else {
          throw new Error('Video stream not ready. Please wait for the camera feed to load and try again.');
        }
      } else {
        // For NVR cameras, use snapshot URL
        const snapshotUrl = cameraService.getCameraSnapshotUrl(parseInt(selectedCamera));
        
        if (!snapshotUrl) {
          throw new Error('Snapshot URL not available.');
        }
        
        console.log('Capturing from snapshot URL:', snapshotUrl);
        const response = await fetch(snapshotUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to capture snapshot');
        }
        
        blob = await response.blob();
      }
      
      // Create file from blob
      const capturedImageUrl = URL.createObjectURL(blob);
      const file = new File([blob], `face-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      setCapturedImage(capturedImageUrl);
      setCapturedFile(file);
    } catch (err) {
      console.error('Failed to capture from camera:', err);
      setError(err instanceof Error ? err.message : 'Failed to capture photo. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };

  // Upload captured photo
  const uploadCapturedPhoto = async () => {
    if (!capturedFile || !id) return;
    
    try {
      setIsUploading(true);
      await personService.uploadFaceImage(parseInt(id), capturedFile, uploadAsPrimary);
      // Refresh face images
      const images = await personService.getFaceImages(parseInt(id));
      setFaceImages(images);
      setSuccessMessage('Photo captured and uploaded successfully');
      // Reset capture state
      setCapturedImage(null);
      setCapturedFile(null);
      setUploadAsPrimary(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload captured photo');
    } finally {
      setIsUploading(false);
    }
  };

  // Clear captured image
  const clearCapturedImage = () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    setCapturedFile(null);
  };

  // Fetch person data for edit mode
  useEffect(() => {
    if (mode === 'edit' && id) {
      const fetchPerson = async () => {
        try {
          setIsLoading(true);
          const personDetail = await personService.getPersonDetail(parseInt(id));
          setFormData({
            person_type: personDetail.person_type,
            first_name: personDetail.first_name,
            last_name: personDetail.last_name,
            employee_id: personDetail.employee_id || '',
            external_company: personDetail.external_company || '',
            phone: personDetail.phone || '',
            email: personDetail.email || '',
            access_level: personDetail.access_level,
            valid_from: personDetail.valid_from ? personDetail.valid_from.split('T')[0] : '',
            valid_until: personDetail.valid_until ? personDetail.valid_until.split('T')[0] : '',
            notes: personDetail.notes || '',
            location_ids: [],
            is_active: personDetail.is_active,
          });
          setFaceImages(personDetail.face_images || []);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch person');
        } finally {
          setIsLoading(false);
        }
      };
      fetchPerson();
    }
  }, [mode, id]);

  // Clear success message after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Photo upload handler
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG or PNG image');
      return;
    }

    try {
      setIsUploading(true);
      await personService.uploadFaceImage(parseInt(id), file, uploadAsPrimary);
      // Refresh face images
      const images = await personService.getFaceImages(parseInt(id));
      setFaceImages(images);
      setSuccessMessage('Photo uploaded successfully');
      setUploadAsPrimary(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Photo delete handler
  const handleDeletePhoto = async (imageId: number) => {
    if (!id) return;
    try {
      await personService.deleteFaceImage(parseInt(id), imageId);
      setFaceImages(prev => prev.filter(img => img.id !== imageId));
      setSuccessMessage('Photo deleted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete photo');
    }
  };

  const handleChange = (field: keyof PersonFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationToggle = (locationId: number) => {
    setFormData(prev => ({
      ...prev,
      location_ids: prev.location_ids.includes(locationId)
        ? prev.location_ids.filter(id => id !== locationId)
        : [...prev.location_ids, locationId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const payload: CreatePersonData = {
        person_type: formData.person_type,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        employee_id: formData.employee_id.trim() || undefined,
        external_company: formData.external_company.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        email: formData.email.trim() || undefined,
        access_level: formData.access_level,
        valid_from: formData.valid_from ? `${formData.valid_from}T00:00:00` : undefined,
        valid_until: formData.valid_until ? `${formData.valid_until}T23:59:59` : undefined,
        notes: formData.notes.trim() || undefined,
        location_ids: formData.location_ids.length > 0 ? formData.location_ids : undefined,
      };

      if (mode === 'create') {
        const created = await personService.createPerson(payload);
        navigate(`/persons/${created.id}/view`);
      } else if (id) {
        await personService.updatePerson(parseInt(id), {
          ...payload,
          is_active: formData.is_active,
        });
        navigate(`/persons/${id}/view`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save person');
    } finally {
      setIsSaving(false);
    }
  };

  const personTypeOptions = [
    { label: 'Employee', value: 'EMPLOYEE' },
    { label: 'Subcontractor', value: 'SUBCONTRACTOR' },
    { label: 'Visitor', value: 'VISITOR' },
  ];

  const accessLevelOptions = [
    { label: 'Full Access', value: 'FULL' },
    { label: 'Limited Access', value: 'LIMITED' },
    { label: 'Escort Required', value: 'ESCORT_REQUIRED' },
    { label: 'No Access', value: 'NONE' },
  ];

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loading size="lg" text="Loading person..." />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/persons')}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">
                {mode === 'create' ? 'Add Person' : 'Edit Person'}
              </h1>
              <p className="text-sm text-text-muted mt-1">
                {mode === 'create' ? 'Register a new employee, subcontractor, or visitor' : 'Update person information'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/persons')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              leftIcon={isSaving ? undefined : <Save size={18} />}
            >
              {isSaving ? 'Saving...' : mode === 'create' ? 'Create Person' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
        {successMessage && <Alert variant="success" message={successMessage} onClose={() => setSuccessMessage(null)} />}

        {/* Basic Information */}
        <Card padding="lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-accent-primary/10 rounded-lg flex items-center justify-center">
              <User size={20} className="text-accent-primary" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary">Basic Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Person Type"
              value={formData.person_type}
              onChange={(e) => handleChange('person_type', e.target.value)}
              options={personTypeOptions}
              required
            />
            
            <Select
              label="Access Level"
              value={formData.access_level}
              onChange={(e) => handleChange('access_level', e.target.value)}
              options={accessLevelOptions}
            />

            <Input
              label="First Name"
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              placeholder="Enter first name"
              required
            />

            <Input
              label="Last Name"
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              placeholder="Enter last name"
              required
            />

            <Input
              label="Employee/Badge ID"
              value={formData.employee_id}
              onChange={(e) => handleChange('employee_id', e.target.value)}
              placeholder="e.g., EMP-001"
            />

            {(formData.person_type === 'SUBCONTRACTOR' || formData.person_type === 'VISITOR') && (
              <Input
                label="External Company"
                value={formData.external_company}
                onChange={(e) => handleChange('external_company', e.target.value)}
                placeholder="Company name"
              />
            )}
          </div>
        </Card>

        {/* Face Photos Section */}
        <Card padding="lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Camera size={20} className="text-purple-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Face Photos</h2>
              <p className="text-sm text-text-muted">Upload clear frontal face photos for recognition</p>
            </div>
          </div>

          {mode === 'create' ? (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 flex items-start gap-3">
              <Info size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-text-secondary">
                <p className="font-medium text-text-primary">Photos can be added after creating the person</p>
                <p className="mt-1">Save this form first, then you'll be able to upload face photos for recognition.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Upload Controls */}
              <div className="flex items-center justify-between p-4 bg-surface-secondary rounded-lg">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadAsPrimary}
                    onChange={(e) => setUploadAsPrimary(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-text-secondary">Set as primary photo</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  leftIcon={isUploading ? undefined : <Upload size={18} />}
                  size="sm"
                >
                  {isUploading ? 'Uploading...' : 'Upload Photo'}
                </Button>
              </div>

              {/* Camera Capture Section */}
              <div className="border border-surface-secondary rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Video size={18} className="text-accent-primary" />
                  <h3 className="font-medium text-text-primary">Capture from Camera</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Location Selection */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Select Location
                    </label>
                    <select
                      value={selectedLocationForCamera}
                      onChange={(e) => {
                        setSelectedLocationForCamera(e.target.value);
                        setSelectedCamera('');
                        setCameraData(null);
                        setCameraStream(null);
                        clearCapturedImage();
                      }}
                      className="w-full px-3 py-2 border border-surface-secondary rounded-lg bg-surface-secondary text-text-primary focus:ring-2 focus:ring-accent-primary/50"
                    >
                      <option value="">Select a location...</option>
                      {locations.map(loc => (
                        <option key={loc.ycl_id} value={loc.ycl_id}>
                          {loc.ycl_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Camera Selection */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Select Camera
                    </label>
                    <select
                      value={selectedCamera}
                      onChange={(e) => {
                        const cameraId = e.target.value;
                        setSelectedCamera(cameraId);
                        clearCapturedImage();
                        if (cameraId) {
                          fetchCameraStream(parseInt(cameraId));
                        } else {
                          setCameraData(null);
                          setCameraStream(null);
                        }
                      }}
                      disabled={!selectedLocationForCamera || cameras.length === 0}
                      className="w-full px-3 py-2 border border-surface-secondary rounded-lg bg-surface-secondary text-text-primary focus:ring-2 focus:ring-accent-primary/50 disabled:opacity-50"
                    >
                      <option value="">
                        {!selectedLocationForCamera
                          ? 'Select location first'
                          : cameras.length === 0
                          ? 'No face cameras available'
                          : 'Select a camera...'}
                      </option>
                      {cameras.map(camera => (
                        <option key={camera.camera_id} value={camera.camera_id}>
                          {camera.name || camera.camera_location} ({camera.camera_purpose})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Camera Stream & Capture */}
                {selectedCamera && cameraData && (cameraStream || cameraData.cloud_provider) && (
                  <div className="space-y-3">
                    {/* Live Stream */}
                    <div id="face-camera-player" className="relative bg-black rounded-lg overflow-hidden" style={{ height: '280px' }}>
                      {cameraData.cloud_provider ? (
                        <EZOPENPlayer
                          camera={{
                            id: cameraData.camera_id,
                            cloud_provider: cameraData.cloud_provider,
                            cloud_serial_no: cameraData.cloud_serial_no,
                            cloud_channel_no: cameraData.cloud_channel_no
                          }}
                          mode="live"
                          showControls={false}
                          autoPlayDelay={0}
                        />
                      ) : cameraStream ? (
                        <VideoPlayer
                          src={cameraStream.player_url || cameraStream.web_player_url || cameraStream.stream_url || cameraStream.webrtc_url || ''}
                          useProxy={false}
                          autoPlay
                          muted
                          controls={false}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          <p>Loading stream...</p>
                        </div>
                      )}
                    </div>

                    {/* Captured Image Preview */}
                    {capturedImage && (
                      <div className="relative">
                        <img
                          src={capturedImage}
                          alt="Captured face"
                          className="w-full rounded-lg"
                          style={{ maxHeight: '200px', objectFit: 'contain' }}
                        />
                        <button
                          type="button"
                          onClick={clearCapturedImage}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                        <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                          ✓ Photo captured - click "Save Captured Photo" to upload
                        </p>
                      </div>
                    )}

                    {/* Capture Buttons */}
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        onClick={captureFromCamera}
                        disabled={isCapturing}
                        variant="secondary"
                        className="flex-1"
                      >
                        {isCapturing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Capturing...
                          </>
                        ) : (
                          <>
                            <Camera className="w-4 h-4 mr-2" />
                            {capturedImage ? 'Retake Photo' : 'Capture Photo'}
                          </>
                        )}
                      </Button>
                      
                      {capturedImage && (
                        <Button
                          type="button"
                          onClick={uploadCapturedPhoto}
                          disabled={isUploading}
                          className="flex-1"
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Save Captured Photo
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Photos Grid */}
              {faceImages.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-surface-secondary rounded-lg">
                  <Camera size={40} className="mx-auto text-text-muted mb-3" />
                  <p className="text-text-muted">No photos uploaded yet</p>
                  <p className="text-sm text-text-muted mt-1">Click "Upload Photo" to add face images</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {faceImages.map(image => (
                    <div
                      key={image.id}
                      className="relative aspect-square rounded-lg overflow-hidden bg-surface-secondary group"
                    >
                      <img
                        src={image.image_url}
                        alt="Face"
                        className="w-full h-full object-cover"
                      />
                      {image.is_primary && (
                        <div className="absolute top-1 left-1 bg-yellow-500 text-white px-1.5 py-0.5 rounded text-xs flex items-center gap-1">
                          <Star size={10} /> Primary
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(image.id)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Contact Information */}
        <Card padding="lg">
          <h2 className="text-lg font-semibold text-text-primary mb-6">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="555-1234"
            />

            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="john@company.com"
            />
          </div>
        </Card>

        {/* Access Period */}
        <Card padding="lg">
          <h2 className="text-lg font-semibold text-text-primary mb-6">Access Period</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Valid From"
              type="date"
              value={formData.valid_from}
              onChange={(e) => handleChange('valid_from', e.target.value)}
            />

            <Input
              label="Valid Until"
              type="date"
              value={formData.valid_until}
              onChange={(e) => handleChange('valid_until', e.target.value)}
              helperText="Leave empty for permanent access"
            />
          </div>

          {mode === 'edit' && (
            <div className="mt-6 pt-6 border-t border-surface-secondary">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  className="w-4 h-4 rounded border-surface-secondary"
                />
                <span className="text-text-primary">Active</span>
                <span className="text-sm text-text-muted">
                  Inactive persons won't be matched during face recognition
                </span>
              </label>
            </div>
          )}
        </Card>

        {/* Location Access (Create mode only) */}
        {mode === 'create' && locations.length > 0 && (
          <Card padding="lg">
            <h2 className="text-lg font-semibold text-text-primary mb-2">Initial Location Access</h2>
            <p className="text-sm text-text-muted mb-6">
              Optionally grant access to locations. You can manage this later from the person's detail page.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {locations.map(location => (
                <label
                  key={location.ycl_id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    formData.location_ids.includes(location.ycl_id)
                      ? 'border-accent-primary bg-accent-primary/5'
                      : 'border-surface-secondary hover:border-surface-hover'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.location_ids.includes(location.ycl_id)}
                    onChange={() => handleLocationToggle(location.ycl_id)}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-text-primary">{location.ycl_name}</span>
                </label>
              ))}
            </div>
          </Card>
        )}

        {/* Notes */}
        <Card padding="lg">
          <h2 className="text-lg font-semibold text-text-primary mb-6">Additional Notes</h2>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Any additional information about this person..."
            rows={4}
            className="w-full px-4 py-3 rounded-lg border border-surface-secondary bg-surface-secondary text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
          />
        </Card>
      </form>
    </MainLayout>
  );
};

export default CreateEditPersonPage;
